import { Turbo } from "@hotwired/turbo-rails"
import { Controller } from "@hotwired/stimulus"
import consumer from "../channels/consumer"

/**
 * Live ingest progress for the construction-drawing pipeline workspace.
 * Subscribes to ConstructionDrawingIngestStatusChannel and patches
 * data-ingest-* nodes without full page reloads.
 *
 * ALSO polls HTTP every 2s while busy (or cable disconnected) because
 * ActionCable/Redis drops were freezing the UI with no recovery.
 */
export default class extends Controller {
  static values = {
    drawingId: Number,
    pollUrl: String,
    pollMs: { type: Number, default: 2000 }
  }

  connect() {
    this.lastRevision = Number(this.element.dataset.cdStateRevision) || 0
    this.renderedRevision = this.lastRevision
    this.hasProgressSnapshot = false
    this.panelsRefreshPending = false
    this.panelsRefreshVersion = 0
    this.wasBusy = false
    this.connected = true
    this.beforeStreamRender = (event) => this.guardStreamRevision(event)
    document.addEventListener("turbo:before-stream-render", this.beforeStreamRender)
    this.state = {}
    this.subscription = null
    this.pollTimer = null
    this.cableConnected = false
    this.pollInFlight = false
    this.panelsRefreshInFlight = false

    if (!this.hasDrawingIdValue || this.drawingIdValue <= 0) return

    this.subscription = consumer.subscriptions.create(
      {
        channel: "ConstructionDrawingIngestStatusChannel",
        drawing_id: this.drawingIdValue
      },
      {
        connected: () => {
          this.cableConnected = true
          this.element.dataset.ingestConnected = "true"
          // One fetch so UI is current even if the subscribe transmit was lost.
          this.pollOnce()
        },
        disconnected: () => {
          this.cableConnected = false
          this.element.dataset.ingestConnected = "false"
          this.ensurePolling()
        },
        received: (data) => this.apply(data)
      }
    )

    this.ensurePolling()
  }

  disconnect() {
    this.connected = false
    document.removeEventListener("turbo:before-stream-render", this.beforeStreamRender)
    window.cancelAnimationFrame(this.patchFrame)
    this.stopPolling()
    if (!this.subscription) return

    consumer.subscriptions.remove(this.subscription)
    this.subscription = null
  }

  ensurePolling() {
    if (this.pollTimer) return
    const ms = Math.max(1000, Number(this.pollMsValue) || 2000)
    this.pollTimer = window.setInterval(() => this.pollOnce(), ms)
    // Immediate first poll
    this.pollOnce()
  }

  stopPolling() {
    if (!this.pollTimer) return
    window.clearInterval(this.pollTimer)
    this.pollTimer = null
  }

  pollUrlResolved() {
    if (this.hasPollUrlValue && this.pollUrlValue) return this.pollUrlValue
    const id = this.drawingIdValue
    return `/construction_drawings/${id}/ingest_progress.json`
  }

  async pollOnce() {
    // Always poll. Skipping when "idle + cable up" froze Identify at 0%
    // because the first payload arrived before task_busy flipped.

    // One request in flight at a time. Under load the progress endpoint can
    // take longer than the 2s interval; without this the fetches stack up and
    // land out of order, so a slow older response overwrites a newer one.
    if (this.pollInFlight) return
    this.pollInFlight = true

    try {
      const res = await fetch(this.pollUrlResolved(), {
        headers: {
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest"
        },
        credentials: "same-origin"
      })
      if (!res.ok) return
      const data = await res.json()
      if (this.connected) this.apply(data)
    } catch (_e) {
      // network blip — next interval retries
    } finally {
      this.pollInFlight = false
    }
  }

  apply(data) {
    if (!data || typeof data !== "object") return
    if (data.drawing_id != null && Number(data.drawing_id) !== this.drawingIdValue) return
    // HTTP and Cable share the persisted drawing revision. A request timestamp
    // is not a state version and must never outrun job broadcasts.
    const revision = Number(data.state_revision)
    if (Number.isFinite(revision)) {
      if (revision < this.lastRevision) return
      this.lastRevision = revision
    }

    const initialSnapshot = !this.hasProgressSnapshot
    this.hasProgressSnapshot = true
    if (data.run_id && this.state.run_id && data.run_id !== this.state.run_id) {
      this.state = {}
      this.requestPanelsRefresh()
    }
    const previous = this.state
    this.state = this.aliasHexapdfKeys({ ...this.state, ...data })
    this.parseHexapdfBindCounts(this.state)
    this.patchState()
    const structuralKeys = [
      "pipeline_outcome", "symbol_pipeline_complete", "takeoff_status", "takeoff_output_count",
      "terminal_task_name", "legends_complete", "sheets_step_state", "legends_step_state",
      "detect_step_state", "ingest_step_state", "review_step_state", "symbols_step_state", "takeoff_step_state"
    ]
    if (initialSnapshot) {
      // The initial HTML already represents renderedRevision. Treat its first
      // progress payload as a baseline, not a completion: replacing the lazy
      // Review frame here aborts its initial load and requests it all over again.
      // Work that completed after the HTML was rendered must still refresh.
      this.wasBusy = this.state.task_busy === true || `${this.state.task_busy}` === "true"
      if (!Number.isFinite(revision) || revision > this.renderedRevision) this.requestPanelsRefresh()
    } else if (structuralKeys.some((key) => previous[key] !== this.state[key])) {
      this.requestPanelsRefresh()
    }
    this.refreshPanelsIfSettled(this.state)
  }

  patchState() {
    this.patchText(this.state)
    this.patchDynamicClasses(this.state)
    this.patchVisibility(this.state)
    this.patchToggles(this.state)
    this.patchProgress(this.state)
    this.patchPhaseStates(this.state)
    this.patchPhaseRailMeta(this.state)
    this.patchBusyState(this.state)
    this.patchStepNav(this.state)
    this.patchAutoStage(this.state)
    this.patchCockpitMode(this.state)
    this.dispatch("progress", { detail: this.state })
  }

  // Storage key is still qcad_vector. Live UI talks HexaPDF native bind.
  aliasHexapdfKeys(data) {
    const pairs = [
      ["qcad_vector_status_slug", "hexapdf_status_slug"],
      ["qcad_vector_status_label", "hexapdf_status_label"],
      ["qcad_vector_status_class", "hexapdf_status_class"],
      ["qcad_vector_message", "hexapdf_message"],
      ["qcad_vector_pct", "hexapdf_pct"],
      ["qcad_vector_progress_label", "hexapdf_progress_label"],
      ["qcad_vector_short", "hexapdf_short"],
      ["qcad_vector_title", "hexapdf_title"],
      ["qcad_vector_bar_class", "hexapdf_bar_class"]
    ]
    pairs.forEach(([from, to]) => {
      if (from in data && !(to in data)) data[to] = data[from]
      if (to in data && !(from in data)) data[from] = data[to]
    })
    return data
  }

  parseHexapdfBindCounts(data) {
    if (`${data.hexapdf_bind_count_label || ""}`.trim()) return data

    const msg = `${data.progress_message || ""} ${data.qcad_vector_message || ""} ${data.hexapdf_message || ""}`
    const match = msg.match(/(\d+)\s+native paths?(?:\s+\((\d+)\s+measured\))?/i)
    if (match) {
      data.hexapdf_bind_count_label = match[2]
        ? `${match[1]} native · ${match[2]} measured`
        : `${match[1]} native paths`
    }
    return data
  }

  patchPhaseRailMeta(data) {
    this.element.querySelectorAll("[data-ingest-phase], [data-phase]").forEach((el) => {
      const key = el.dataset.ingestPhase || el.dataset.phase
      if (!key) return

      const title =
        data[`${key}_title`] ||
        data[`${key}_label`] ||
        (key === "qcad_vector" ? data.hexapdf_title : null)
      if (title) el.setAttribute("title", title)

      const short =
        data[`${key}_short`] ||
        (key === "qcad_vector" ? data.hexapdf_short : null)
      const titleEl = el.querySelector(".cd-phase-rail-title")
      if (short && titleEl && !titleEl.dataset.ingestText) {
        titleEl.textContent = short
      }
    })
  }

  patchCockpitMode(data) {
    // Remove retired synthetic substeps from pages kept open across deployment.
    const retired = ["legends_omega", "legends_parse", "legends_crops"]
    const selectors = retired.flatMap((key) => [`[data-ingest-phase="${key}"]`, `[data-phase="${key}"]`]).join(", ")
    this.element.querySelectorAll(selectors).forEach((el) => el.remove())
    // Job-start navigation is autoStageForTask. The viewed tab owns the rail
    // so Sheets/Legends/Detect/Review/Take-off each keep their own phases.
    this.resyncShellRail(`${data.cockpit_mode || ""}`.trim())
  }

  resyncShellRail(fallbackMode) {
    let mode = `${fallbackMode || ""}`.trim()
    if (mode === "identify") mode = "sheets"
    try {
      const shell = this.application.getControllerForElementAndIdentifier(this.element, "cd-shell")
      if (shell && typeof shell.syncCockpitRail === "function") {
        const stage =
          typeof shell.currentStage === "function" ? shell.currentStage() : mode
        shell.syncCockpitRail(stage || mode)
        return
      }
    } catch (_e) {
      /* fall through */
    }
    if (!mode) return
    this.element.querySelectorAll("[data-cockpit-rail]").forEach((el) => {
      el.hidden = el.dataset.cockpitRail !== mode
    })
  }

  patchStepNav(data) {
    const stageToStateKey = {
      sheets: ["sheets_step_state"],
      legends: ["legends_step_state"],
      detect: ["detect_step_state", "ingest_step_state"],
      review: ["review_step_state", "symbols_step_state"],
      takeoff: ["takeoff_step_state"]
    }
    this.element.querySelectorAll(".cd-stage-tab[data-stage]").forEach((tab) => {
      const keys = stageToStateKey[tab.dataset.stage] || []
      const key = keys.find((k) => k in data)
      if (!key) return
      const state = `${data[key] ?? ""}`.trim()
      if (!state) return
      const wasCurrent = tab.classList.contains("is-current")
      tab.className = tab.className
        .split(/\s+/)
        .filter((c) => c && !c.startsWith("is-"))
        .concat(`is-${state}`)
        .join(" ")
      if (wasCurrent) tab.classList.add("is-current")
    })
  }

  guardStreamRevision(event) {
    const stream = event.detail?.newStream || event.target
    if (Number(stream?.dataset?.cdDrawingId) !== this.drawingIdValue) return
    const revision = Number(stream.dataset.cdStateRevision)
    if (!Number.isFinite(revision)) return
    if (revision < this.lastRevision) {
      event.preventDefault()
      this.requestPanelsRefresh()
      return
    }
    this.lastRevision = revision
    // Turbo inserts fresh nodes after the event. Reapply the latest JSON state
    // to those nodes, without starting another panel request.
    const render = event.detail.render
    if (typeof render !== "function") return
    event.detail.render = async (element) => {
      // A newer poll can arrive while Turbo is waiting to render this stream.
      if (revision < this.lastRevision) {
        this.requestPanelsRefresh()
        return
      }
      try {
        await render(element)
      } catch (error) {
        this.requestPanelsRefresh()
        throw error
      }
      window.cancelAnimationFrame(this.patchFrame)
      this.patchFrame = window.requestAnimationFrame(() => {
        if (this.connected && Number(this.state.state_revision) >= revision) this.patchState()
      })
    }
  }

  requestPanelsRefresh() {
    this.panelsRefreshPending = true
    this.panelsRefreshVersion = (this.panelsRefreshVersion || 0) + 1
  }

  refreshPanelsIfSettled(data) {
    const busy = data.task_busy === true || `${data.task_busy}` === "true"
    if (this.wasBusy !== busy) this.requestPanelsRefresh()
    this.wasBusy = busy
    // Progress is patched from JSON; rebuilding every table on every heartbeat
    // replaces live controls and can put old pending phases back on screen.
    if (busy || !this.panelsRefreshPending || this.panelsRefreshInFlight) return
    this.refreshPipelinePanels()
  }

  async refreshPipelinePanels() {
    const id = this.drawingIdValue
    if (!id) return
    this.panelsRefreshInFlight = true
    const requestedRun = this.state.run_id
    const requestedVersion = this.panelsRefreshVersion
    try {
      const res = await fetch(`/construction_drawings/${id}/pipeline_panels`, {
        headers: {
          Accept: "text/vnd.turbo-stream.html",
          "X-Requested-With": "XMLHttpRequest"
        },
        credentials: "same-origin"
      })
      if (!res.ok) return
      const revision = Number(res.headers.get("X-CD-State-Revision"))
      const html = await res.text()
      if (!this.connected) return
      if (Number.isFinite(revision) && revision < this.lastRevision) return
      if (requestedRun && this.state.run_id && requestedRun !== this.state.run_id) return
      if (html) {
        // Turbo renders streams asynchronously. A later completion or rejected
        // stale stream must remain pending instead of being acknowledged by
        // the earlier request. Clear before insertion so render guards can retry.
        this.panelsRefreshPending = this.panelsRefreshVersion !== requestedVersion
        Turbo.renderStreamMessage(html)
        this.resyncShellRail()
      }
    } catch (_e) {
      // next poll retries if still needed
      this.requestPanelsRefresh()
    } finally {
      this.panelsRefreshInFlight = false
    }
  }

  // Switch mission-control stage when a job starts so Detect/Take-off isn't hidden.
  patchAutoStage(data) {
    const busy = data.task_busy === true || `${data.task_busy}` === "true"
    const name =
      (!busy && data.terminal_task_name) ||
      data.active_background_task_name ||
      data.active_task_name ||
      data.task_name ||
      ""
    try {
      const shell = this.application.getControllerForElementAndIdentifier(this.element, "cd-shell")
      if (shell && typeof shell.autoStageForTask === "function") {
        const legendsComplete =
          data.legends_complete === true || `${data.legends_complete}` === "true"
        shell.autoStageForTask(name, {
          busy,
          complete:
            !busy &&
            (data.symbol_pipeline_complete === true ||
              data.pipeline_complete === true ||
              (name.toLowerCase() === "legend_catalog" && legendsComplete)),
          legendsComplete
        })
      }
    } catch (_e) {
      /* ignore */
    }
  }

  // —— DOM patches ——

  patchText(data) {
    this.element.querySelectorAll("[data-ingest-text]").forEach((el) => {
      const key = el.dataset.ingestText
      if (!key || !(key in data)) return
      el.textContent = `${data[key] ?? ""}`
    })
  }

  patchDynamicClasses(data) {
    this.element.querySelectorAll("[data-ingest-dynamic-class]").forEach((el) => {
      const key = el.dataset.ingestDynamicClass
      if (!key || !(key in data)) return

      const previous = (el.dataset.ingestCurrentClass || "").trim()
      if (previous) {
        previous.split(/\s+/).forEach((c) => el.classList.remove(c))
      }

      const next = `${data[key] ?? ""}`.trim()
      if (next) {
        next.split(/\s+/).forEach((c) => el.classList.add(c))
      }
      el.dataset.ingestCurrentClass = next
    })
  }

  patchVisibility(data) {
    this.element.querySelectorAll("[data-ingest-show-when-present]").forEach((el) => {
      const key = el.dataset.ingestShowWhenPresent
      if (!key || !(key in data)) return

      const hidden = `${data[key] ?? ""}`.trim().length === 0
      el.classList.toggle("is-hidden", hidden)
      el.classList.toggle("d-none", hidden)
    })
  }

  // Boolean-driven show/hide. Used by the pipeline failure banner so a stage
  // that fails mid-poll surfaces immediately instead of only on a full reload.
  // data-ingest-toggle-invert="true" shows the element when the flag is false
  // (e.g. LIVE pill is hidden while idle).
  patchToggles(data) {
    this.element.querySelectorAll("[data-ingest-toggle]").forEach((el) => {
      const key = el.dataset.ingestToggle
      if (!key || !(key in data)) return

      const raw = data[key]
      const flag = raw === true || raw === "true" || raw === 1 || raw === "1"
      const invert = el.dataset.ingestToggleInvert === "true"
      const show = invert ? !flag : flag
      const hideClass = el.dataset.ingestToggleClass || "d-none"
      el.classList.toggle(hideClass, !show)
      el.classList.toggle("d-none", !show)
      el.classList.toggle("is-hidden", !show)
    })
  }

  patchProgress(data) {
    this.element.querySelectorAll("[data-ingest-progress]").forEach((el) => {
      const key = el.dataset.ingestProgress
      if (!key || !(key in data)) return

      const raw = Number(data[key])
      const percent = Number.isFinite(raw) ? Math.max(0, Math.min(100, raw)) : 0
      el.style.width = `${percent}%`

      const bar = el.closest('[role="progressbar"]') || el
      bar.setAttribute("aria-valuenow", String(percent))

      const labelKey = `${key.replace(/_pct$/, "")}_progress_label`
      const altLabelKey = `${key}_label`
      const wrap = el.closest(".cd-phase") || el.closest(".cd-step-progress") || el.closest(".cd-symbol-ingest-progress")
      if (labelKey in data) {
        const labelEl =
          wrap?.querySelector(`[data-ingest-text="${labelKey}"]`) ||
          wrap?.querySelector(".cd-step-progress-pct")
        if (labelEl && labelEl.dataset.ingestText === labelKey) {
          // text node already handled by patchText
        } else if (wrap?.querySelector(".cd-step-progress-pct")) {
          wrap.querySelector(".cd-step-progress-pct").textContent = `${data[labelKey]}`
        }
      } else if (altLabelKey in data) {
        const pctEl = wrap?.querySelector(".cd-step-progress-pct")
        if (pctEl) pctEl.textContent = `${data[altLabelKey]}`
      } else {
        const pctEl = wrap?.querySelector(".cd-step-progress-pct")
        if (pctEl) pctEl.textContent = `${percent}%`
      }
    })
  }

  // Highlight active/completed workflow phases (data-ingest-phase / data-phase).
  // Prefer *_status_slug from payload so "skipped (not needed)" never becomes
  // class "is-skipped_(not_needed)".
  patchPhaseStates(data) {
    const phases = this.element.querySelectorAll("[data-ingest-phase], [data-phase]")
    if (!phases.length) return

    const PHASE_STATE_CLASSES = [
      "is-pending",
      "is-running",
      "is-processing",
      "is-completed",
      "is-skipped",
      "is-failed",
      "is-active",
      "is-queued",
      "is-ready",
      "is-locked"
    ]

    phases.forEach((el) => {
      const key = el.dataset.ingestPhase || el.dataset.phase
      if (!key) return

      const slugKey = `${key}_status_slug`
      const statusKey = `${key}_status_label`
      const visibilityKey = `${key}_visible`
      const hasState = slugKey in data || statusKey in data ||
        (key === "qcad_vector" && ("hexapdf_status_slug" in data || "hexapdf_status_label" in data))
      // Compact progress intentionally omits expensive Review fields. Preserve
      // those server-rendered states instead of inventing a pending status.
      if (!hasState) {
        if (visibilityKey in data) {
          el.hidden = data[visibilityKey] === false || /^(skip|deselect|disabled)/.test(el.dataset.ingestStatusSlug || "")
        }
        return
      }
      const hexapdfSlug = key === "qcad_vector" ? `${data.hexapdf_status_slug ?? ""}`.trim().toLowerCase() : ""
      let slug = `${data[slugKey] ?? hexapdfSlug}`.trim().toLowerCase()
      if (!slug) {
        const hexapdfLabel = key === "qcad_vector" ? data.hexapdf_status_label : null
        const raw = `${data[statusKey] ?? hexapdfLabel ?? "pending"}`
          .trim()
          .toLowerCase()
        slug = this.normalizePhaseSlug(raw)
      }

      PHASE_STATE_CLASSES.forEach((c) => el.classList.remove(c))
      if (visibilityKey in data) {
        el.hidden = data[visibilityKey] === false || /^(skip|deselect|disabled)/.test(slug)
      } else if (/^(skip|deselect|disabled)/.test(slug)) {
        el.hidden = true
      }
      if (slug) el.classList.add(`is-${slug}`)
      if (slug === "running" || slug === "processing") el.classList.add("is-active")
      el.dataset.ingestStatusSlug = slug || "pending"
    })
  }

  normalizePhaseSlug(raw) {
    const s = `${raw || "pending"}`.trim().toLowerCase()
    if (!s) return "pending"
    if (s.startsWith("skip")) return "skipped"
    if (s.startsWith("run") || s === "processing") return "running"
    if (s.startsWith("complete") || s === "done") return "completed"
    if (s.startsWith("fail") || s === "error") return "failed"
    if (s.startsWith("queue")) return "queued"
    if (s === "ready") return "ready"
    if (s === "locked") return "locked"
    if (s.startsWith("pend") || s === "idle") return "pending"
    // first token only — never full phrases as class names
    return s.split(/[\s(/]/)[0] || "pending"
  }

  patchBusyState(data) {
    const busy = data.task_busy === true || `${data.task_busy}` === "true"
    const learningBusy =
      busy &&
      (`${data.learning_stage || ""}` === "processing" ||
        ("learning_pct" in data && Number(data.learning_pct) < 100))

    this.element.querySelectorAll("[data-ingest-disabled]").forEach((el) => {
      const key = el.dataset.ingestDisabled
      if (!key || !(key in data)) return
      const disabled = data[key] === true || `${data[key]}` === "true"
      el.dataset.ingestBusy = disabled ? "true" : "false"
      // When task becomes idle, clear sticky force_disabled so Run re-enables.
      if (!disabled && el.dataset.forceDisabled === "true") {
        el.dataset.forceDisabled = "false"
      }
      el.disabled = disabled || el.dataset.forceDisabled === "true"
    })

    this.element.querySelectorAll('[data-ingest-text="active_background_task_button_label"]').forEach((el) => {
      if (!el.dataset.ingestIdleLabel) {
        el.dataset.ingestIdleLabel = el.textContent.trim()
      }
      const label = `${data.active_background_task_button_label ?? ""}`.trim()
      el.textContent = busy && label.length > 0 ? label : el.dataset.ingestIdleLabel
    })

    const learningInProgress =
      `${data.learning_stage || ""}` === "processing" ||
      (`${data.active_background_task_state || ""}` === "processing" &&
        `${data.active_background_task_label || ""}`.toLowerCase().includes("vision"))

    this.element.querySelectorAll("[data-ingest-progress='overall_pct']").forEach((el) => {
      el.classList.toggle("is-busy", busy && !learningInProgress)
      el.classList.toggle("progress-bar-animated", busy && !learningInProgress)
      el.classList.toggle("progress-bar-striped", busy && !learningInProgress)
    })

    this.element.querySelectorAll("[data-ingest-progress='learning_pct']").forEach((el) => {
      el.classList.toggle("is-busy", learningBusy)
      el.classList.toggle("progress-bar-animated", learningBusy)
      el.classList.toggle("progress-bar-striped", learningBusy)
      if (!learningBusy && Number(data.learning_pct) >= 100) {
        el.classList.remove("bg-warning")
        el.classList.add("bg-success")
      }
    })

    this.element.querySelectorAll("[data-ingest-progress='identify_overall_pct']").forEach((el) => {
      const identifyBusy = busy && `${data.identify_stage_label || ""}`.includes("identif")
      el.classList.toggle(
        "is-busy",
        identifyBusy || (busy && "identify_overall_pct" in data && Number(data.identify_overall_pct) < 100)
      )
    })

    const hexapdfBusy = ["running", "processing"].includes(
      `${data.qcad_vector_status_slug || data.hexapdf_status_slug || ""}`.toLowerCase()
    )
    this.element.querySelectorAll("[data-ingest-progress='qcad_vector_pct'], [data-ingest-progress='hexapdf_pct']").forEach((el) => {
      el.classList.toggle("is-busy", busy && hexapdfBusy)
      el.classList.toggle("progress-bar-animated", busy && hexapdfBusy)
      el.classList.toggle("progress-bar-striped", busy && hexapdfBusy)
    })

    this.element.classList.toggle("cd-workspace-busy", busy)

    if ("detect_running" in data) {
      const detecting = data.detect_running === true || `${data.detect_running}` === "true"
      this.element.querySelectorAll(".cd-symbol-ingest-progress, .cd-sip").forEach((el) => {
        el.classList.toggle("is-busy", detecting)
      })
    }

    this.ensurePolling()
  }
}
